import React from "react";
import { useIntl } from "react-intl";
import classNames from "classnames";
import { Skill } from "@/model";
import { clickFromKeyboard } from "@/util/keyboardUtil";
import { useDeleteSkill } from "@/query/skill";
import { useDialog, useModal } from "@/hook";
import { errorToast, successToast } from "@/component/Toast";
import { Popover, PopoverButton, PopoverItem, PopoverMenu } from "@/component/Popover";
import { DeleteIcon, EditIcon } from "@/assets/svgs/icons";

import { SKILL_MODIFY_MODAL_ID } from "./SkillModifyModal";


interface SkillTableProps {
    skills: Skill[];
    selectedSkill?: Skill;
    onClick: (skill?: Skill) => void;
    onDelete: (skill: Skill) => void;
}

const SkillTable: React.FC<SkillTableProps> = (props) => {
    const { skills, selectedSkill, onClick, onDelete } = props;
    const intl = useIntl();

    return (
        <div className="w-full max-h-full flex flex-col border border-black-200 bg-white rounded-[6px] overflow-hidden">
            <div className="min-h-table-row pl-[25px] flex justify-start items-center border-b border-black-200 text-black-500 text-[14px] font-[600] leading-[1.5]">
                {intl.formatMessage({ id: "i03020" })}
            </div>
            <div className="overflow-auto">
                {skills.map((skill, index) => (
                    <SkillFragment key={skill.id}
                                   skill={skill}
                                   isSelected={skill.id === selectedSkill?.id}
                                   isLast={index === (skills.length - 1)}
                                   onClick={onClick}
                                   onDelete={onDelete}
                    />
                ))}
            </div>
            {skills.length === 0 && (
                <div className="empty-table">
                    {intl.formatMessage({ id: "b0018" })}
                </div>
            )}
        </div>
    );
};

interface SkillFragmentProps {
    skill: Skill;
    isLast: boolean;
    isSelected: boolean;
    onClick: (skill: Skill) => void;
    onDelete: (skill: Skill) => void;
}

const SkillFragment: React.FC<SkillFragmentProps> = (props) => {
    const { skill, isLast, isSelected, onClick, onDelete } = props;
    const intl = useIntl();
    const modal = useModal();
    const dialog = useDialog();

    const { mutateAsync: deleteSkill } = useDeleteSkill(skill.agent_id, skill.id);

    const onClickDelete = () => {
        dialog.open({
            variant: "danger",
            title: intl.formatMessage({ id: "i03012" }),
            content: intl.formatMessage({ id: "i03013" }),
            confirmText: intl.formatMessage({ id: "b0008" }),
            onConfirm: async () => {
                deleteSkill()
                    .then(() => {
                        successToast(intl.formatMessage({ id: "t003" }));
                        onDelete(skill);
                    })
                    .catch(() => {
                        errorToast(intl.formatMessage({ id: "t004" }));
                    });
            }
        });
    };

    return (
        <div tabIndex={0}
             className={classNames(
                "popover-text-400 group w-full min-h-table-row pl-[25px] pr-2.5 flex justify-between items-center cursor-pointer",
                isLast ?
                    "rounded-b-[6px]"
                    : "border-b border-black-150",
                isSelected
                    ? "bg-chat42-5"
                    : "hover:bg-black-100 focus:bg-black-100"
            )}
             onClick={() => onClick(skill)}
             onKeyDown={clickFromKeyboard}
        >
            <div className="line-clamp-2">
                 {skill.name}
            </div>

            <Popover>
                {({ open, close }) => (
                    <>
                        <PopoverButton className="size-4 invisible group-hover:visible group-focus-within:visible data-[open]:visible"
                                       open={open}
                                       close={close}
                        />
                        <PopoverMenu>
                            <PopoverItem className="selection-table-popover-item"
                                         onClick={() => modal.open(SKILL_MODIFY_MODAL_ID, skill)}
                            >
                                <EditIcon className="size-3.5" />
                                {intl.formatMessage({ id: "i03011" })}
                            </PopoverItem>
                            <PopoverItem className="selection-table-popover-item text-point-red"
                                         onClick={onClickDelete}
                            >
                                <DeleteIcon className="size-3.5 stroke-point-red" />
                                {intl.formatMessage({ id: "i03012" })}
                            </PopoverItem>
                        </PopoverMenu>
                    </>
                )}
            </Popover>
        </div>
    );
};

export default SkillTable;
